import type { LargeUpsConfigurationsSaveHandle } from './subsections/largeUpsConfigurationsSaveHandle';
import type { SelectedFile } from '../../../components/forms/FileUpload';
import type { RepeatableInstanceSaveHandle } from '../../../components/repeatableSections/repeatableInstanceSaveHandle';
import type { SavedAttachment } from '../../../services/attachmentService';
import type { Request } from '../../../types';
import type { LargeUpsConfiguration } from '../../../types/largeUps';

export interface LargeUpsInitialProps {
  formData: Record<string, unknown> & {
    stage?: string;
    sysRequestStage?: string;
  };
  updateFormData: (
    field: string,
    value: string | number | boolean | LargeUpsConfiguration[] | undefined,
  ) => void;
  requestId?: string;
  disabled?: boolean;
  attachmentsBySection?: Record<string, SavedAttachment[]>;
  onAttachFiles?: (sectionId: string) => void;
  onDeleteAttachment?: (attachmentId: number) => void;
  isEditing?: boolean;
  selectedFiles?: Record<string, SelectedFile[]>;
  onFilesSelected?: (sectionId: string, files: SelectedFile[]) => void;
  offlineMode?: boolean;
  showFullDetails?: boolean;
  enabledStepIds?: readonly string[];
  registerLargeUpsSaveHandle?: (
    handle: LargeUpsConfigurationsSaveHandle | null,
  ) => void;
  onLargeUpsEditorActiveChange?: (active: boolean) => void;
}

export interface LargeUpsPersistedProps {
  request: Request;
  isEditing: boolean;
  editedData: Record<string, unknown>;
  updateEditedData: (
    field: string,
    value: string | number | boolean | undefined,
  ) => void;
  showFullDetails?: boolean;
  showViewToggle?: boolean;
  isAdvancedView?: boolean;
  onToggleView?: () => void;
  attachmentsBySection?: Record<string, SavedAttachment[]>;
  onAttachFiles?: (sectionId: string) => void;
  onDeleteAttachment?: (attachmentId: number) => void;
  selectedFiles?: Record<string, SelectedFile[]>;
  onFilesSelected?: (sectionId: string, files: SelectedFile[]) => void;
  offlineMode?: boolean;
  onCaptionChange?: (attachmentId: number, caption: string) => void;
  pendingCaptionEdits?: Record<number, string>;
  enabledStepIds?: readonly string[];
  /**
   * Request Detail engine wiring (#997 Story 6.5). The engine section registers
   * its save handle with the screen's `useLargeUpsEngineSaveBridge` and writes
   * the live session list into `editedData.largeUpsConfigurations` via
   * `applyEditedDataPatch` so it survives step navigation.
   */
  registerInstanceSaveHandle?: (
    handle: RepeatableInstanceSaveHandle | null,
  ) => void;
  applyEditedDataPatch?: (patch: Record<string, unknown>) => void;
  /** True when `editedData.largeUpsConfigurations` was touched this edit session. */
  largeUpsConfigurationsDirty?: boolean;
  onLargeUpsEditorActiveChange?: (active: boolean) => void;
}

export type LargeUpsProps = LargeUpsInitialProps | LargeUpsPersistedProps;

export function isLargeUpsPersistedProps(
  p: LargeUpsProps,
): p is LargeUpsPersistedProps {
  return 'request' in p && (p as LargeUpsPersistedProps).request != null;
}
